/**
 * Security boundary for administrator-defined HTML customization.
 *
 * Only safe attributes/styles are returned to React. Event handlers, script
 * URLs and executable markup are intentionally removed to reduce XSS risk.
 */
import type { CSSProperties } from 'react';

const ATTRIBUTE_NAME = /^[A-Za-z_:][A-Za-z0-9:._-]*$/;
const BLOCKED_ATTRIBUTE = /^(?:on|srcdoc$)/i;
const SAFE_CONTROL_ATTRIBUTES = new Set([
  'autocomplete', 'inputmode', 'pattern', 'min', 'max', 'step', 'dir', 'lang',
  'spellcheck', 'title', 'role', 'aria-label', 'aria-describedby', 'aria-labelledby',
  'aria-live', 'aria-controls', 'aria-expanded'
]);
const SAFE_WRAPPER_ATTRIBUTES = new Set(['dir', 'lang', 'title', 'role', 'aria-label', 'aria-describedby', 'aria-labelledby', 'aria-live']);
const REACT_ATTRIBUTE_NAMES: Record<string, string> = {
  autocomplete: 'autoComplete',
  inputmode: 'inputMode',
  spellcheck: 'spellCheck',
  tabindex: 'tabIndex'
};

/** Parses one `name=value` attribute per line into a normalized dictionary. */
export function parseAttributeLines(text: string): Record<string, string> {
  const result: Record<string, string> = {};
  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line || line.startsWith('#')) continue;
    const separator = line.indexOf('=');
    const rawName = separator < 0 ? line : line.slice(0, separator);
    const name = rawName.trim();
    if (!ATTRIBUTE_NAME.test(name) || BLOCKED_ATTRIBUTE.test(name)) continue;
    const rawValue = separator < 0 ? 'true' : line.slice(separator + 1).trim();
    result[name] = rawValue.replace(/^(['"])(.*)\1$/, '$2');
  }
  return result;
}

export function formatAttributeLines(attributes?: Record<string, string>): string {
  return Object.entries(attributes ?? {}).map(([name, value]) => `${name}=${value}`).join('\n');
}

function isSafeUrl(value: string): boolean {
  return !/^\s*(?:javascript|vbscript|data:text\/html)/i.test(value);
}

function safeAttributes(
  attributes: Record<string, string> | undefined,
  explicit: Set<string>
): Record<string, string | boolean | number> {
  const output: Record<string, string | boolean | number> = {};
  for (const [rawName, rawValue] of Object.entries(attributes ?? {})) {
    const name = rawName.trim().toLowerCase();
    if (!ATTRIBUTE_NAME.test(name) || BLOCKED_ATTRIBUTE.test(name)) continue;
    if (!(name.startsWith('data-') || name.startsWith('aria-') || explicit.has(name))) continue;
    if ((name === 'href' || name === 'src' || name === 'formaction') && !isSafeUrl(rawValue)) continue;
    const reactName = REACT_ATTRIBUTE_NAMES[name] ?? name;
    // Keep administrator-entered values as strings. Converting every literal
    // "true"/"false" to booleans is incorrect for attributes such as
    // aria-expanded, data-* and spellcheck and could silently remove "false".
    output[reactName] = rawValue;
  }
  return output;
}

/** Filters custom attributes for the primary control; executable/event attributes are rejected. */
export function safeControlAttributes(attributes?: Record<string, string>): Record<string, string | boolean | number> {
  return safeAttributes(attributes, SAFE_CONTROL_ATTRIBUTES);
}

/** Filters attributes for the field wrapper using the same allow/deny policy. */
export function safeWrapperAttributes(attributes?: Record<string, string>): Record<string, string | boolean | number> {
  return safeAttributes(attributes, SAFE_WRAPPER_ATTRIBUTES);
}

/** Converts safe inline CSS declarations to React's CSSProperties object. */
export function parseInlineStyle(styleText?: string): CSSProperties | undefined {
  if (!styleText?.trim()) return undefined;
  const output: Record<string, string> = {};
  for (const declaration of styleText.split(';')) {
    const separator = declaration.indexOf(':');
    if (separator <= 0) continue;
    const rawName = declaration.slice(0, separator).trim();
    const value = declaration.slice(separator + 1).trim();
    if (!rawName || !value || /expression\s*\(|javascript:/i.test(value)) continue;
    const name = rawName.startsWith('--')
      ? rawName
      : rawName.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase());
    output[name] = value;
  }
  return Object.keys(output).length ? output as CSSProperties : undefined;
}

/**
 * Lightweight sanitization for administrator-provided before/after snippets.
 * It deliberately strips executable elements and inline event handlers.
 */
/** Removes scripts, event handlers and unsafe URLs from administrator-supplied before/after HTML. */
export function sanitizeCustomHtml(html?: string): string {
  if (!html?.trim() || typeof DOMParser === 'undefined') return html ?? '';
  const parser = new DOMParser();
  const document = parser.parseFromString(`<div id="df-custom-root">${html}</div>`, 'text/html');
  const root = document.getElementById('df-custom-root');
  if (!root) return '';
  root.querySelectorAll('script,object,embed,base,meta,link').forEach((element) => element.remove());
  for (const element of Array.from(root.querySelectorAll('*'))) {
    for (const attribute of Array.from(element.attributes)) {
      const name = attribute.name.toLowerCase();
      if (BLOCKED_ATTRIBUTE.test(name) || ((name === 'href' || name === 'src' || name === 'formaction') && !isSafeUrl(attribute.value))) {
        element.removeAttribute(attribute.name);
      }
    }
  }
  return root.innerHTML;
}
